// OTE (Overtime Exemption) — the CCIS verification report, drawn ONLY from the frozen snapshot written by ote-actions
// 'sign' (never from live data). Printed to PDF by netlify/functions/render-ote-background.js via /ote-print/:token.
//   OTEReport       CCIS verification report (kept by CCIS — not sent to the client): cover → details table (as the
//                   CCIS Risk Assessment Verification Report) → activities → checklist → items for the client → observations
//                   & conclusion → attendance → documents → photos.
// The MOM endorsement is not drawn here: it is MOM's own Excel form, filled in by netlify/functions/_ote/fillEndorsement.js.
// Photos: private files — the renderer passes short-lived links in window.__OTE_ASSETS__ (by frozen path).
import React from 'react'

const TEAL = '#0e5c5c', INK = '#1d2b2b', MUTED = '#5c6b6a', LINE = '#d6e0df', SOFT = '#f2f7f7'
const FONT = '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif'
const table = { width: 'calc(100% - 2px)', margin: '0 1px', borderCollapse: 'collapse', tableLayout: 'fixed' }
const cell = { border: `1px solid ${LINE}`, padding: '5px 7px', verticalAlign: 'top', fontSize: 10.5, lineHeight: 1.4, color: INK, wordWrap: 'break-word', whiteSpace: 'normal' }
const th = { ...cell, background: TEAL, borderColor: TEAL, color: '#fff', fontWeight: 600, textAlign: 'center', verticalAlign: 'bottom' }
const label = { ...cell, background: SOFT, fontWeight: 600, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, verticalAlign: 'middle' }
const val = { ...cell, fontSize: 11.5 }
const band = { ...cell, fontWeight: 700, background: SOFT, color: TEAL }
const h2 = { fontSize: 14, fontWeight: 700, letterSpacing: '0.02em', color: TEAL, margin: '20px 0 8px', borderBottom: `1px solid ${LINE}`, paddingBottom: 4 }
const NEW_PAGE = { breakBefore: 'page', pageBreakBefore: 'always' }
const KEEP = { pageBreakInside: 'avoid', breakInside: 'avoid' }
const SECTIONS = { A: 'Risk Assessment', B: 'Risk Evaluation and Controls', C: 'Overtime-Specific Risk Assessment', D: 'Communication', E: 'MOM OTE Eligibility' }

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const sgParts = d => { const s = new Date(d).toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' }); return s.split('-').map(Number) }
export const longDate = d => { if (!d) return '—'; const [y, m, dd] = String(d).length === 10 ? String(d).split('-').map(Number) : sgParts(d); return `${dd} ${MONTHS[m - 1]} ${y}` }
const time = d => d ? new Date(d).toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore', hour: 'numeric', minute: '2-digit' }) : '—'
const sgTime = d => new Date(d).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })
const asset = p => (p && typeof window !== 'undefined' && window.__OTE_ASSETS__ && window.__OTE_ASSETS__[p]) || null
const resultColor = r => r === 'Satisfactory' ? '#1d6b3a' : r === 'Not Satisfactory' ? '#a32020' : MUTED

function Cover({ s, logos }) {
  return (
    <section style={{ breakAfter: 'page', pageBreakAfter: 'always', minHeight: '262mm', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: 90 }}>{logos?.company && <img src={logos.company} alt="CCIS" style={{ maxHeight: 64, maxWidth: 220, objectFit: 'contain' }} />}</div>
      <div style={{ marginTop: '55mm' }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: TEAL, lineHeight: 1.25 }}>RISK ASSESSMENT VERIFICATION REPORT</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: TEAL, marginTop: 6 }}>OVERTIME EXEMPTION</div>
        <div style={{ height: 4, width: 120, background: TEAL, margin: '18px 0' }} />
        <div style={{ fontSize: 20, fontWeight: 600, color: INK }}>{s.company?.name}</div>
        <div style={{ fontSize: 13, marginTop: 18, lineHeight: 1.8, color: MUTED }}>
          <div>Issued On: {longDate(s.signer?.signed_at || s.closed_by_office?.at)}</div>
          <div>Ref No.: {s.ref_no || '—'}</div>
        </div>
      </div>
    </section>
  )
}

function Details({ s, logos }) {
  const cols = <colgroup><col style={{ width: '24%' }} /><col style={{ width: '30%' }} /><col style={{ width: '20%' }} /><col style={{ width: '26%' }} /></colgroup>
  return (
    <section>
      <div style={{ ...h2, marginTop: 0 }}>Risk Assessment Verification Report</div>
      <table style={table}>{cols}<tbody>
        <tr><td style={label}>Name of Company</td><td style={val} colSpan={3}>{s.company?.name}</td></tr>
        <tr><td style={label}>Location of Verification</td><td style={{ ...val, whiteSpace: 'pre-wrap' }} colSpan={3}>{s.company?.address || '—'}</td></tr>
        <tr><td style={label}>UEN Number</td><td style={val}>{s.company?.uen || '—'}</td><td style={label}>Workplace Number</td><td style={val}>{s.workplace_no || '—'}</td></tr>
        <tr><td style={label}>Normative References</td><td style={val} colSpan={3}>Workplace Safety and Health (Risk Management) Regulations<br />Overtime Exemption</td></tr>
        <tr><td style={label}>Name of Auditing Firm</td><td style={val} colSpan={3}>{s.firm || 'CCIS Singapore Pte Ltd'}</td></tr>
        <tr><td style={label}>Date of Verification</td><td style={val}>{longDate(s.audit_date)}</td><td style={label}>Time Started</td><td style={val}>{time(s.start_at)}</td></tr>
        <tr><td style={label}>Name of Auditor</td><td style={val}>{s.auditor}</td><td style={label}>Time Ended</td><td style={val}>{time(s.end_at)}</td></tr>
        <tr>
          <td style={label}>Signature of Auditor</td>
          <td style={val}>{s.signer
            ? <><div>{s.signer.name}</div><div style={{ fontSize: 9.5, color: '#666', marginTop: 1 }}>(Electronically signed on {sgTime(s.signer.signed_at)}{s.signer.ip ? ` · IP ${s.signer.ip}` : ''})</div></>
            : <div style={{ fontSize: 10.5, color: '#666' }}>Not signed — closed by the office ({s.closed_by_office?.name}, {sgTime(s.closed_by_office?.at)}) because the client did not close out the outstanding items.</div>}</td>
          <td style={label}>Auditing Organisation’s Stamp</td>
          <td style={{ ...val, textAlign: 'center' }}>{logos?.stamp && <img src={logos.stamp} alt="Company stamp" style={{ maxHeight: 90, maxWidth: 140, objectFit: 'contain' }} />}</td>
        </tr>
      </tbody></table>

      {s.business_nature && <><div style={h2}>A. Company’s Profile</div><p style={{ fontSize: 12, margin: 0 }}>{s.business_nature}</p></>}
      <div style={h2}>B. Objectives</div>
      <p style={{ fontSize: 12, lineHeight: 1.6, margin: 0 }}>The objective of this verification is to verify that {s.company?.name} has conducted risk assessment for the work activities undertaken during the Overtime Exemption period, and that these activities are of low risk and are not listed in Annex A of MOM’s Overtime Exemption application.</p>
      <div style={h2}>C. Scope</div>
      <p style={{ fontSize: 12, lineHeight: 1.6, margin: 0 }}>The verification covers the Risk Assessment / Risk Register for the work activities listed in Section D.</p>
    </section>
  )
}

function Activities({ s }) {
  return (
    <section style={NEW_PAGE}>
      <div style={{ ...h2, marginTop: 0 }}>D. Work Activities Undertaken During the Overtime Exemption Period</div>
      <table style={table}>
        <colgroup><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /><col style={{ width: '9%' }} /><col style={{ width: '29%' }} /><col style={{ width: '14%' }} /><col style={{ width: '9%' }} /><col style={{ width: '9%' }} /></colgroup>
        <thead><tr><th style={th}>Occupation</th><th style={th}>Department</th><th style={th}>No. Affected</th><th style={th}>Work Activities</th><th style={th}>RA Reference</th><th style={th}>Final Risk</th><th style={th}>In Annex A?</th></tr></thead>
        <tbody>{(s.activities || []).map((r, i) => (
          <tr key={i} style={KEEP}>
            <td style={cell}>{r.occupation}</td><td style={cell}>{r.department}</td><td style={{ ...cell, textAlign: 'center' }}>{r.no_affected}</td>
            <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{r.work_activities}</td><td style={cell}>{r.ra_reference}</td>
            <td style={{ ...cell, textAlign: 'center', fontWeight: 700, color: r.risk_level === 'Low' ? '#1d6b3a' : '#a32020' }}>{r.risk_level === 'Low' ? 'LOW' : r.risk_level || '—'}</td>
            <td style={{ ...cell, textAlign: 'center' }}>{r.annex_a === false ? 'No' : r.annex_a ? 'Yes' : '—'}</td>
          </tr>))}</tbody>
      </table>
      {!!(s.removed_activities || []).length && <>
        <div style={{ fontSize: 12, fontWeight: 700, color: TEAL, margin: '14px 0 6px' }}>Activities not included in the endorsement</div>
        <table style={table}><colgroup><col style={{ width: '40%' }} /><col style={{ width: '20%' }} /><col style={{ width: '40%' }} /></colgroup>
          <thead><tr><th style={th}>Work Activities</th><th style={th}>Reason</th><th style={th}>Note</th></tr></thead>
          <tbody>{s.removed_activities.map((r, i) => <tr key={i} style={KEEP}><td style={cell}>{r.work_activities}</td><td style={cell}>{r.reason}</td><td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{r.note || ''}</td></tr>)}</tbody>
        </table></>}
    </section>
  )
}

function Checklist({ s }) {
  let last = null
  return (
    <section style={NEW_PAGE}>
      <div style={{ ...h2, marginTop: 0 }}>E. Verification Checklist ({s.revision})</div>
      <table style={table}>
        <colgroup><col style={{ width: '7%' }} /><col style={{ width: '43%' }} /><col style={{ width: '16%' }} /><col style={{ width: '34%' }} /></colgroup>
        <thead><tr><th style={th}>No.</th><th style={th}>Items</th><th style={th}>Result</th><th style={th}>Findings / Remarks</th></tr></thead>
        <tbody>{(s.items || []).flatMap(it => {
          const rows = []
          if (it.section !== last) { last = it.section; rows.push(<tr key={'s' + it.section} style={KEEP}><td style={band}>{it.section}</td><td style={band} colSpan={3}>{SECTIONS[it.section]}</td></tr>) }
          const sub = it.parts && it.factors ? it.parts.map(p => `${p.label}: ${it.factors[p.key] || '—'}`).join('\n') : ''
          rows.push(
            <tr key={it.code} style={KEEP}>
              <td style={{ ...cell, fontWeight: 700 }}>{it.code}</td>
              <td style={cell}><div style={{ fontWeight: 700 }}>{it.title}</div><div style={{ color: '#444', marginTop: 2 }}>{it.verify}</div>{sub && <div style={{ whiteSpace: 'pre-wrap', marginTop: 4, color: MUTED }}>{sub}</div>}</td>
              <td style={{ ...cell, textAlign: 'center', fontWeight: 700, color: resultColor(it.result) }}>{it.result || '—'}</td>
              <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{it.finding || ''}</td>
            </tr>)
          return rows
        })}</tbody>
      </table>
    </section>
  )
}

function Conclusion({ s }) {
  const issues = s.issues || []
  const ok = s.conclusion === 'SATISFACTORY'
  return (
    <section>
      {!!issues.length && <>
        <div style={h2}>F. Items Raised with the Client</div>
        <table style={table}>
          <colgroup><col style={{ width: '7%' }} /><col style={{ width: '33%' }} /><col style={{ width: '25%' }} /><col style={{ width: '35%' }} /></colgroup>
          <thead><tr><th style={th}>Item</th><th style={th}>Finding</th><th style={th}>Action Needed</th><th style={th}>Close-out</th></tr></thead>
          <tbody>{issues.map((i, n) => (
            <tr key={n} style={KEEP}>
              <td style={cell}>{i.item_code || '—'}</td><td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{i.finding}</td><td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{i.client_action || ''}</td>
              <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{i.status === 'closed' ? `${i.closure_comment || ''}\n(${i.closed_by || ''}, ${longDate(i.closed_at)})` : 'NOT CLOSED'}</td>
            </tr>))}</tbody>
        </table></>}
      <div style={h2}>{issues.length ? 'G' : 'F'}. Observations</div>
      <p style={{ fontSize: 12, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{s.observations || '—'}</p>
      <div style={h2}>{issues.length ? 'H' : 'G'}. Conclusion</div>
      <div style={{ ...KEEP, border: `2px solid ${ok ? '#1d6b3a' : '#a32020'}`, padding: '10px 12px', fontSize: 12, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: ok ? '#1d6b3a' : '#a32020' }}>{s.conclusion}</div>
        {ok
          ? <div>The verification shows that {s.company?.name} has completed the risk assessment for the work activities listed in Section D; these activities are of LOW risk and are not listed in Annex A. The Risk Register is endorsed (Effective Date of Risk Assessment Report: {longDate(s.effective_date)}).</div>
          : <><div>The Risk Register is NOT endorsed, for the following reason(s):</div><ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{(s.reasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul></>}
        {s.ra_reason && <div style={{ marginTop: 6, color: MUTED }}>RA version accepted (not the latest): {s.ra_reason}</div>}
      </div>
    </section>
  )
}

function Records({ s }) {
  const att = s.attendance || []
  const docs = s.documents || []
  const photos = (s.photos || []).filter(p => asset(p.frozen_path))
  const KIND = { ra: 'Risk Assessment / Risk Register', swp: 'Safe Work Procedure', other: 'Other evidence' }
  return (
    <section style={NEW_PAGE}>
      <div style={{ ...h2, marginTop: 0 }}>Annex 1 — Attendance</div>
      <table style={table}><colgroup><col style={{ width: '40%' }} /><col style={{ width: '40%' }} /><col style={{ width: '20%' }} /></colgroup>
        <thead><tr><th style={th}>Name</th><th style={th}>Designation</th><th style={th}>Meeting</th></tr></thead>
        <tbody>{att.map((p, i) => <tr key={i} style={KEEP}><td style={cell}>{p.name}</td><td style={cell}>{p.designation}</td><td style={{ ...cell, textAlign: 'center' }}>{p.meeting === 'closing' ? 'Closing' : 'Opening'}</td></tr>)}</tbody>
      </table>

      <div style={h2}>Annex 2 — Documents Reviewed</div>
      <table style={table}><colgroup><col style={{ width: '24%' }} /><col style={{ width: '40%' }} /><col style={{ width: '14%' }} /><col style={{ width: '22%' }} /></colgroup>
        <thead><tr><th style={th}>Type</th><th style={th}>File</th><th style={th}>Version / Date</th><th style={th}>Fingerprint (SHA-256)</th></tr></thead>
        <tbody>{docs.map((d, i) => (
          <tr key={i} style={KEEP}>
            <td style={cell}>{KIND[d.kind] || d.kind}{d.kind === 'ra' && d.accepted ? ' (accepted)' : ''}</td>
            <td style={cell}>{d.label ? `${d.label} — ` : ''}{d.file_name}</td>
            <td style={cell}>{d.version_no ? `v${d.version_no}` : ''}{d.effective_date ? ` ${longDate(d.effective_date)}` : ''}</td>
            <td style={{ ...cell, fontSize: 7.5, fontFamily: 'monospace' }}>{d.sha256}</td>
          </tr>))}</tbody>
      </table>
      {!(docs.some(d => d.kind === 'swp')) && s.swp_reason && <p style={{ fontSize: 11, margin: '6px 0 0' }}>No SWP attached: {s.swp_reason}</p>}
      <p style={{ fontSize: 10, color: MUTED, margin: '6px 0 0' }}>Copies of the documents follow at the end of this report.</p>

      <div style={h2}>Annex 3 — Photographs</div>
      {!photos.length && <p style={{ fontSize: 11, margin: 0 }}>{s.no_photo_reason ? `No session photo: ${s.no_photo_reason}` : 'No photographs.'}</p>}
      {s.photo_consent && !!photos.length && <p style={{ fontSize: 10, color: MUTED, margin: '0 0 6px' }}>Photo consent: {typeof s.photo_consent === 'string' ? s.photo_consent : 'obtained'}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4mm' }}>
        {photos.map((p, i) => (
          <figure key={i} style={{ ...KEEP, margin: 0, width: '88mm' }}>
            <img src={asset(p.frozen_path)} alt="" style={{ width: '88mm', height: '66mm', objectFit: 'contain', background: '#fafafa', border: `1px solid ${LINE}` }} />
            <figcaption style={{ fontSize: 9, color: MUTED }}>{p.kind === 'session_photo' ? 'Session photo' : 'Implementation photo'}</figcaption>
          </figure>))}
      </div>
    </section>
  )
}

export function OTEReport({ snapshot: s, logos }) {
  return (
    <div style={{ fontFamily: FONT, color: INK }}>
      <Cover s={s} logos={logos} />
      <Details s={s} logos={logos} />
      <Activities s={s} />
      <Checklist s={s} />
      <Conclusion s={s} />
      <Records s={s} />
    </div>
  )
}
